"use client";

import { createContext, useContext, type ReactNode } from "react";

interface FieldContextValue {
  id: string;
  required: boolean;
  invalid: boolean;
  describedBy?: string | undefined;
}

const FieldContext = createContext<FieldContextValue | null>(null);

export interface FormFieldProps {
  /** Stable id shared with the nested TextInput, SelectInput, or TextArea. */
  id: string;
  label: ReactNode;
  children: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  className?: string;
}

export function FormField({
  id,
  label,
  children,
  hint,
  error,
  required = false,
  className,
}: FormFieldProps) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <FieldContext.Provider value={{ id, required, invalid: Boolean(error), describedBy }}>
      <div className={`flex min-w-0 flex-col gap-1.5 ${className ?? ""}`}>
        <label htmlFor={id} className="text-sm font-medium text-text-primary">
          {label}
          {required ? <span aria-hidden="true" className="ml-1 text-danger">*</span> : null}
        </label>
        {children}
        {hint ? <p id={hintId} className="text-sm text-text-secondary">{hint}</p> : null}
        {error ? <p id={errorId} role="alert" className="text-sm text-danger-dark">{error}</p> : null}
      </div>
    </FieldContext.Provider>
  );
}

export function useFormFieldA11y({
  id,
  required,
  describedBy,
  invalid,
}: {
  id?: string | undefined;
  required?: boolean | undefined;
  describedBy?: string | undefined;
  invalid?: boolean | "false" | "true" | "grammar" | "spelling" | undefined;
}) {
  const field = useContext(FieldContext);
  const mergedDescribedBy = [field?.describedBy, describedBy]
    .filter((value): value is string => Boolean(value))
    .join(" ") || undefined;

  return {
    id: field?.id ?? id,
    required: required ?? field?.required,
    "aria-describedby": mergedDescribedBy,
    "aria-invalid": invalid ?? (field?.invalid ? true : undefined),
  };
}

export function isAriaInvalid(value: boolean | "false" | "true" | "grammar" | "spelling" | undefined): boolean {
  return value === true || value === "true" || value === "grammar" || value === "spelling";
}
